import { Controller } from "@hotwired/stimulus"

// Reports this page view and the time spent on it for the admin dashboard (POST /page-visits,
// PageVisitsController). A page view gets a random visit key; the browser keeps an anonymous
// visitor id in localStorage, the same one the interview sign-in funnel uses (iq_subject_controller).
// Time counts only while the tab is visible and the visitor has done something (moved, scrolled,
// typed, touched) in the last IDLE_MS, so a tab left open in the background doesn't add up.
const VISITOR_KEY = "iq-visitor"
const TICK_MS = 5000
const REPORT_EVERY_MS = 15000
const IDLE_MS = 60000
const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "scroll", "touchstart", "wheel"]

export default class extends Controller {
  connect() {
    if (!navigator.sendBeacon && !window.fetch) return

    this.visit = this.randomId()
    this.visitor = this.visitorId()
    this.seconds = 0
    this.reported = -1
    this.lastActive = Date.now()
    this.lastTick = Date.now()

    this.onActivity = () => { this.lastActive = Date.now() }
    this.onHide = () => { if (document.visibilityState === "hidden") this.report() }
    this.onPageHide = () => this.report()
    ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, this.onActivity, { passive: true }))
    document.addEventListener("visibilitychange", this.onHide)
    window.addEventListener("pagehide", this.onPageHide)

    this.ticker = setInterval(() => this.tick(), TICK_MS)
    this.reporter = setInterval(() => this.report(), REPORT_EVERY_MS)
    const query = new URLSearchParams(window.location.search)
    const utm = Object.fromEntries(["utm_source", "utm_medium", "utm_campaign"].map((key) => [key, query.get(key) || ""]))
    this.report({ path: window.location.pathname, referrer: document.referrer, ...utm })
  }

  disconnect() {
    clearInterval(this.ticker)
    clearInterval(this.reporter)
    ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, this.onActivity))
    document.removeEventListener("visibilitychange", this.onHide)
    window.removeEventListener("pagehide", this.onPageHide)
  }

  tick() {
    const now = Date.now()
    if (document.visibilityState === "visible" && now - this.lastActive < IDLE_MS) {
      this.seconds += Math.round((now - this.lastTick) / 1000)
    }
    this.lastTick = now
  }

  // The first report (with the path) creates the visit; later ones send the seconds so far,
  // and only when they changed.
  report(extra = null) {
    if (!extra && this.seconds === this.reported) return
    this.reported = this.seconds
    const body = JSON.stringify({ visit: this.visit, visitor: this.visitor, seconds: this.seconds, ...extra })
    const blob = new Blob([body], { type: "application/json" })
    if (navigator.sendBeacon && navigator.sendBeacon("/page-visits", blob)) return

    fetch("/page-visits", { method: "POST", body, keepalive: true, headers: { "Content-Type": "application/json" } }).catch(() => {})
  }

  visitorId() {
    let visitor = null
    try { visitor = JSON.parse(window.localStorage.getItem(VISITOR_KEY)) } catch { /* not stored */ }
    if (!/^[a-z0-9]{16}$/.test(visitor || "")) {
      visitor = this.randomId()
      try { window.localStorage.setItem(VISITOR_KEY, JSON.stringify(visitor)) } catch { /* kept for this page only */ }
    }
    return visitor
  }

  randomId() {
    return Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) => (byte % 36).toString(36)).join("")
  }
}
